import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowUp,
  ChevronsUpDown,
  Inbox,
  Layers,
  LayoutGrid,
  List,
  MessageSquare,
  Plane,
  Rows3,
  SlidersHorizontal,
  Tag,
  UserCheck,
  UserX,
  X,
} from 'lucide-react';
import { api, type Me, type TenantConfig, type TicketListRow } from '../lib/api.js';
import { ago, minutes, personLabel } from '../lib/format.js';
import { shortQueue, shortRequestType } from '../lib/labels.js';
import { canAccess, hasCapability } from '../lib/access.js';
import { isSettled, slaConsumed, slaVerdict } from '../lib/sla.js';
import { useRowSwipe } from '../lib/useRowSwipe.js';
import { queueKeyAction } from '../lib/queueKeys.js';
import { useChangedRows } from '../lib/useChangedRows.js';
import { legalTransitionsFrom } from '@esa/shared';
import { queryForView, VIEWS, type View } from '../lib/queueViews.js';
import { awayLabel } from '../lib/away.js';
import {
  AccessDenied,
  Avatar,
  Badge,
  Button,
  ChipGroup,
  EmptyState,
  ErrorState,
  Kbd,
  Menu,
  MenuContent,
  MenuLabel,
  MenuRadioGroup,
  MenuRadioItem,
  MenuTrigger,
  Segmented,
  Select,
  Skeleton,
  Chilli,
  PriorityIcon,
  SlaRing,
  Tooltip,
  EventBadge,
  FamilyBadge,
  VipBadge,
  cn,
} from '../ui/index.js';
import { TicketPeek } from '../components/TicketPeek.js';
import { Board } from '../components/Board.js';
import { TicketContextMenu, useTicketActions } from '../components/TicketActions.js';
import { BulkBar } from '../components/BulkBar.js';
import { SavedViews, type ViewState } from '../components/SavedViews.js';
import { RowColors } from '../components/RowColors.js';
import { useQueuePrefs } from '../lib/useQueuePrefs.js';
import { ROW_COLOR_HEX, rowTint, rowTintClear } from '../lib/rowColors.js';
import { rowColorFor, type SavedView } from '@esa/shared';

interface Ctx {
  tenant: string;
  role: string | null;
  tenantName: string | undefined;
}

const SORTS = [
  { value: 'updated_desc', label: 'Recently updated' },
  { value: 'created_desc', label: 'Newest first' },
  { value: 'created_asc', label: 'Oldest first' },
  { value: 'due_asc', label: 'Due soonest' },
  { value: 'number_desc', label: 'Ticket number' },
];

export function Queue() {
  const { tenant, role, tenantName } = useOutletContext<Ctx>();
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();

  const view = (params.get('view') as View | null) ?? 'open';
  /**
   * Whose queue this is, when it is not yours.
   *
   * A URL parameter rather than page state so it is linkable: the reports
   * screen sends a lead here from "Sam is holding 40", and the link has to
   * survive being pasted into a message about it.
   */
  const assigneeOid = params.get('assignee');
  // A tag clicked on a row: only the tickets carrying it.
  const tagFilter = params.get('tag');
  const mode = (params.get('mode') as 'list' | 'board' | null) ?? 'list';
  const [queueKey, setQueueKey] = useState('');
  const [requestType, setRequestType] = useState('');
  const [sort, setSort] = useState('updated_desc');
  const [density, setDensity] = useState<'compact' | 'normal'>('normal');
  const [cursor, setCursor] = useState(0);
  const [peekNumber, setPeekNumber] = useState<number | null>(null);
  const rowRefs = useRef<(HTMLTableRowElement | null)[]>([]);

  const allowed = canAccess(role, 'queue');

  /**
   * This person's own views and row colors on this desk (087).
   *
   * `cameFrom` is the saved view last chosen, so after changing a filter the
   * menu can offer to update that view rather than only to save a new one.
   */
  const queuePrefs = useQueuePrefs(tenant, allowed);
  const [cameFrom, setCameFrom] = useState<string | null>(null);
  const currentView: ViewState = {
    view,
    mode,
    sort,
    density,
    ...(queueKey ? { queueKey } : {}),
    ...(requestType ? { requestType } : {}),
    ...(assigneeOid ? { assigneeOid } : {}),
    ...(tagFilter ? { tag: tagFilter } : {}),
  };
  const applyView = (v: SavedView) => {
    const next = new URLSearchParams(params);
    for (const k of ['view', 'mode', 'assignee', 'tag']) next.delete(k);
    if (v.view && v.view !== 'open') next.set('view', v.view);
    if (v.mode && v.mode !== 'list') next.set('mode', v.mode);
    if (v.assigneeOid) next.set('assignee', v.assigneeOid);
    if (v.tag) next.set('tag', v.tag);
    setParams(next, { replace: true });
    setSort(v.sort || 'updated_desc');
    setQueueKey(v.queueKey ?? '');
    setRequestType(v.requestType ?? '');
    setDensity(v.density ?? 'normal');
    setCursor(0);
    setCameFrom(v.id);
  };

  /**
   * START WHERE THEY CHOSE. Opening the queue plainly -- from the menu, or
   * on signing in -- puts their "Start here" view up, once per desk per
   * visit. A link that already says what to show (a report's "Sam is
   * holding 40") is left alone: somebody followed it to see that.
   */
  const startedOn = useRef<string | null>(null);
  useEffect(() => {
    if (!queuePrefs.loaded || startedOn.current === tenant) return;
    startedOn.current = tenant;
    const plain = !['view', 'mode', 'assignee', 'tag'].some((k) => params.has(k));
    const start = queuePrefs.prefs.views.find((v) => v.id === queuePrefs.prefs.defaultViewId);
    if (plain && start) applyView(start);
    // Once per desk, when the preferences arrive; not on every change of them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queuePrefs.loaded, tenant]);

  const setParam = (k: string, v: string | null) => {
    const next = new URLSearchParams(params);
    if (v === null) next.delete(k);
    else next.set(k, v);
    setParams(next, { replace: true });
  };

  const setView = (v: View) => {
    const next = new URLSearchParams(params);
    if (v === 'open') next.delete('view');
    else next.set('view', v);
    setParams(next, { replace: true });
    setCursor(0);
  };

  /**
   * Who has a ticket open right now, across the whole desk.
   *
   * Whole-desk rather than "these eighty ids": the set is a handful, so it is
   * smaller than the question would be, and it does not change when the
   * reader scrolls, sorts or filters — which means this poll is independent
   * of the list query and never invalidates it.
   *
   * Twenty seconds, matching the beat. Cheap by construction: one indexed
   * read of a table the beat keeps swept. No model call — worth saying on
   * this screen, where a poll once re-ran an AI summary every thirty seconds.
   */
  const viewing = useQuery({
    queryKey: ['viewing', tenant],
    queryFn: () =>
      api.get<{ byTicket: Record<string, { oid: string; name: string | null; email: string | null }[]> }>(
        `/api/v1/tenants/${tenant}/tickets/viewing`,
      ),
    enabled: Boolean(tenant) && allowed,
    refetchInterval: 20_000,
    // Presence is a courtesy; a failed poll must not paint an error over a
    // working queue.
    retry: false,
  });
  const viewersOf = (n: number) => viewing.data?.byTicket?.[String(n)] ?? [];

  const cfg = useQuery({
    queryKey: ['config', tenant],
    queryFn: () => api.get<TenantConfig>(`/api/v1/tenants/${tenant}/config`),
    enabled: Boolean(tenant) && allowed,
  });

  // After `cfg`: the resolve dialog needs the group's field definitions to
  // know what is required at close.
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.get<Me>('/api/v1/me') });
  const actions = useTicketActions(tenant, cfg.data, me.data);
  // One instance for the table: only one row can be dragged at a time, and a
  // hook cannot be called inside the row loop.
  const swipe = useRowSwipe();
  // Bulk changes and deleted tickets, for whoever may on this desk.
  const canBulk = hasCapability(me.data, tenant, 'ticket.bulk');
  const canDelete = hasCapability(me.data, tenant, 'ticket.delete');
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const togglePick = (n: number) =>
    setPicked((cur) => {
      const next = new Set(cur);
      if (next.has(n)) next.delete(n);
      else next.add(n);
      return next;
    });
  const counts = useQuery({
    queryKey: ['queue-counts', tenant],
    queryFn: () => api.get<Record<string, number>>(`/api/v1/tenants/${tenant}/queue-counts`),
    enabled: Boolean(tenant) && allowed,
  });

  /**
   * Where we are in the list, as a stack of keyset cursors.
   *
   * A stack rather than a page number because the pagination is keyset:
   * "everything after this row" has no notion of page 3, so going back means
   * remembering where each page started. Reset whenever the query changes,
   * or Next would resume from a position in a different result set.
   */
  const [pages, setPages] = useState<string[]>([]);
  const pageCursor = pages.at(-1) ?? null;

  const q = queryForView({
    cursor: pageCursor,
    view,
    sort,
    mode,
    meOid: me.data?.oid ?? null,
    assigneeOid,
    queueKey,
    requestType,
    tag: tagFilter,
  });

  // A cursor names a row in the result set it came from, so a changed query
  // makes the whole stack meaningless — resuming from it would page through
  // a list that no longer exists.
  const queryKey = q.toString();
  const lastKey = useRef(queryKey);
  useEffect(() => {
    const withoutCursor = new URLSearchParams(queryKey);
    withoutCursor.delete('cursor');
    const prior = new URLSearchParams(lastKey.current);
    prior.delete('cursor');
    if (withoutCursor.toString() !== prior.toString() && pages.length > 0) setPages([]);
    lastKey.current = queryKey;
  }, [queryKey, pages.length]);

  const list = useQuery({
    queryKey: ['tickets', tenant, q.toString()],
    queryFn: () =>
      api.get<{ tickets: TicketListRow[]; nextCursor: string | null }>(
        `/api/v1/tenants/${tenant}/tickets?${q.toString()}`,
      ),
    enabled: Boolean(tenant) && allowed,
  });

  const rows = useMemo(() => list.data?.tickets ?? [], [list.data]);

  /**
   * Rows that changed under the reader since the last refetch.
   *
   * Scoped to the query: switching filter or sort replaces the whole list,
   * and those rows are a different question rather than a set of events. The
   * hook resets its baseline silently when that key changes.
   */
  const changedRows = useChangedRows(rows, `${tenant}|${q.toString()}`);

  /**
   * Keyboard navigation. j/k and the arrows move a cursor, Enter opens the
   * peek pane, o opens the full ticket, Escape closes. This is the single
   * cheapest thing that makes a queue feel like a tool rather than a report.
   */
  const move = useCallback(
    (delta: number) => {
      setCursor((c) => {
        const next = Math.max(0, Math.min(rows.length - 1, c + delta));
        rowRefs.current[next]?.scrollIntoView({ block: 'nearest' });
        return next;
      });
    },
    [rows.length],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const action = queueKeyAction(e);
      if (action === null) return;
      const t = rows[cursor];

      if (action === 'down') {
        e.preventDefault();
        move(1);
      } else if (action === 'up') {
        e.preventDefault();
        move(-1);
      } else if (action === 'peek') {
        if (t) {
          e.preventDefault();
          setPeekNumber(t.number);
        }
      } else if (action === 'open') {
        // preventDefault here too: without it the keypress also reaches
        // whatever had focus, which on a row link is a second navigation.
        if (t) {
          e.preventDefault();
          nav(`/t/${tenant}/${t.number}`);
        }
      } else if (action === 'close') {
        setPeekNumber(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [rows, cursor, move, nav, tenant]);

  // Every hook has run by here, so gating the render cannot change hook order.
  if (!allowed) {
    return <AccessDenied area="queue" role={role} tenantName={tenantName} />;
  }

  /** Click a header to sort; click again to flip direction. */
  const SORTABLE: Record<string, { asc: string; desc: string }> = {
    ticket: { asc: 'created_asc', desc: 'number_desc' },
    sla: { asc: 'due_asc', desc: 'due_asc' },
    updated: { asc: 'created_asc', desc: 'updated_desc' },
  };
  const headerSort = (key: string) => {
    const opt = SORTABLE[key];
    if (!opt) return;
    setSort((cur) => (cur === opt.desc ? opt.asc : opt.desc));
  };
  const sortIcon = (key: string) => {
    const opt = SORTABLE[key];
    if (!opt) return null;
    if (sort === opt.desc) return <ArrowDown className="h-3 w-3" />;
    if (sort === opt.asc) return <ArrowUp className="h-3 w-3" />;
    return (
      <ChevronsUpDown className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-60" />
    );
  };

  const activeFilters = [queueKey, requestType].filter(Boolean).length;

  /**
   * Every filter currently narrowing the list, named. Used by the empty state,
   * because "no tickets" and "no tickets THAT MATCH" are different facts and
   * only one of them is the reader's fault.
   */
  const activeFilterLabels = [
    view !== 'open' ? VIEWS.find((v) => v.value === view)?.label : null,
    queueKey ? `queue: ${cfg.data?.queues.find((q) => q.key === queueKey)?.name ?? queueKey}` : null,
    requestType ? `type: ${shortRequestType(requestType)}` : null,
  ].filter((x): x is string => Boolean(x));

  const clearFilters = () => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const k of ['view', 'queueKey', 'requestType']) next.delete(k);
        return next;
      },
      { replace: true },
    );
  };
  const rowH = density === 'compact' ? 'h-11' : 'h-14';

  return (
    // min-h-full, not h-full: the row has to grow with the list so the peek
    // pane beside it has somewhere to stick. At h-full the row was exactly one
    // viewport tall while the list overflowed past it, which is why the pane
    // scrolled off the top and never came back.
    <div className="flex min-h-full min-w-0">
      {/* ---------------------------------------------------------------- */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Sticky command bar: title, stats, filters. */}
        <div className="sticky top-0 z-20 border-b border-line bg-page/85 backdrop-blur-md">
          <div className="flex flex-wrap items-end justify-between gap-3 px-5 pb-3 pt-4">
            <div className="min-w-0">
              <h1 className="flex items-center gap-2 text-lg font-semibold leading-tight text-ink">
                {cfg.data?.name ?? tenantName ?? 'Queue'}
                {canDelete && (
                  <Link to="/queue/deleted" className="text-2xs font-normal text-ink-tertiary hover:text-ink-secondary">
                    Deleted tickets
                  </Link>
                )}
                {/* WHOSE QUEUE THIS IS, and a way out of it.
                    A filter that changes what you are looking at without
                    saying so is how somebody concludes the desk has four
                    tickets. The name comes off the rows rather than a second
                    fetch or a URL parameter that could disagree with them —
                    and when there are none, "no open tickets" is the answer
                    to the question anyway. */}
                {assigneeOid && (
                  <span className="flex items-center gap-1.5 rounded-full border border-line bg-surface px-2 py-0.5 text-xs font-normal text-ink-secondary">
                    <UserCheck aria-hidden className="h-3 w-3" />
                    {rows[0]?.assignee_name ?? 'one person'}
                    <button
                      type="button"
                      onClick={() => setParam('assignee', null)}
                      className="rounded-xs text-ink-tertiary transition-colors hover:text-ink"
                      aria-label="Show the whole desk again"
                      title="Show the whole desk again"
                    >
                      <X aria-hidden className="h-3 w-3" />
                    </button>
                  </span>
                )}
                {tagFilter && (
                  <span className="flex items-center gap-1.5 rounded-full border border-line bg-surface px-2 py-0.5 text-xs font-normal text-ink-secondary">
                    <Tag aria-hidden className="h-3 w-3" />
                    {tagFilter}
                    <button
                      type="button"
                      onClick={() => setParam('tag', null)}
                      className="rounded-xs text-ink-tertiary transition-colors hover:text-ink"
                      aria-label="Show every tag again"
                      title="Show every tag again"
                    >
                      <X aria-hidden className="h-3 w-3" />
                    </button>
                  </span>
                )}
              </h1>
              <p className="mt-0.5 flex items-center gap-2 text-xs text-ink-secondary">
                <span>{rows.length} shown</span>
                {counts.data && (
                  <>
                    <span aria-hidden className="text-ink-tertiary">·</span>
                    <span className="nums">{counts.data.open} open</span>
                    {(counts.data.breached ?? 0) > 0 && (
                      <>
                        <span aria-hidden className="text-ink-tertiary">·</span>
                        <span className="nums text-danger">{counts.data.breached} overdue</span>
                      </>
                    )}
                  </>
                )}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Segmented
                aria-label="View mode"
                size="sm"
                className="w-[9.5rem]"
                value={mode}
                onChange={(v) => setParam('mode', v === 'list' ? null : v)}
                options={[
                  {
                    value: 'list',
                    label: (
                      <span className="flex items-center gap-1">
                        <List className="h-3 w-3" />
                        List
                      </span>
                    ),
                  },
                  {
                    value: 'board',
                    label: (
                      <span className="flex items-center gap-1">
                        <LayoutGrid className="h-3 w-3" />
                        Board
                      </span>
                    ),
                  },
                ]}
              />
              {activeFilters > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  leading={<X className="h-3 w-3" />}
                  onClick={() => {
                    setQueueKey('');
                    setRequestType('');
                  }}
                >
                  Clear {activeFilters}
                </Button>
              )}

              <SavedViews
                prefs={queuePrefs.prefs}
                current={currentView}
                cameFrom={cameFrom}
                onApply={applyView}
                onSave={(next) => queuePrefs.save.mutate(next)}
                saving={queuePrefs.save.isPending}
              />
              <Select
                size="sm"
                aria-label="Filter by queue"
                leading={<Layers />}
                value={queueKey}
                onValueChange={setQueueKey}
                placeholder="All queues"
                className="w-[10.5rem]"
                options={[
                  { value: '', label: 'All queues' },
                  ...(cfg.data?.queues.map((x) => ({ value: x.key, label: x.name })) ?? []),
                ]}
              />
              <Select
                size="sm"
                aria-label="Filter by request type"
                leading={<SlidersHorizontal />}
                value={requestType}
                onValueChange={setRequestType}
                placeholder="All types"
                className="w-[12rem]"
                options={[
                  { value: '', label: 'All request types' },
                  ...(cfg.data?.requestTypes.map((rt) => ({
                    value: rt.label,
                    label: shortRequestType(rt.label),
                    hint: rt.label,
                  })) ?? []),
                ]}
              />
              <Select
                size="sm"
                aria-label="Sort"
                value={sort}
                onValueChange={setSort}
                options={SORTS}
                className="w-[9.5rem]"
              />
              {mode === 'list' && (
                <Menu>
                <MenuTrigger asChild>
                  <Button variant="secondary" size="sm" iconOnly aria-label="Row density">
                    <Rows3 className="h-3.5 w-3.5" />
                  </Button>
                </MenuTrigger>
                <MenuContent>
                  <MenuLabel>Row height</MenuLabel>
                  <MenuRadioGroup
                    value={density}
                    onValueChange={(v) => setDensity(v as 'compact' | 'normal')}
                  >
                    <MenuRadioItem value="compact">Compact</MenuRadioItem>
                    <MenuRadioItem value="normal">Comfortable</MenuRadioItem>
                  </MenuRadioGroup>
                </MenuContent>
                </Menu>
              )}
              <RowColors
                prefs={queuePrefs.prefs}
                cfg={cfg.data}
                onSave={(next) => queuePrefs.save.mutate(next)}
                saving={queuePrefs.save.isPending}
              />
            </div>
          </div>

          <div className="px-5 pb-3">
            <ChipGroup
              aria-label="Queue views"
              value={view}
              onChange={setView}
              // "Owner away" only when somebody is: a tab that reads 0 on
              // most days is a tab people learn to skip.
              options={VIEWS.filter((v) => v.value !== 'away' || view === 'away' || (counts.data?.away ?? 0) > 0).map((v) => ({
                value: v.value,
                label: v.label,
                title: v.title,
                count: counts.data?.[v.countKey],
              }))}
            />
          </div>
        </div>

        {/* ---------------------------------------------------------------- */}
        <div className="min-w-0 flex-1 px-5 py-4">
          {list.error && <ErrorState error={list.error} />}

          {list.isLoading && (
            <div className="overflow-hidden rounded-lg border border-line bg-surface">
              {Array.from({ length: 12 }).map((_, i) => (
                <div
                  key={i}
                  className={cn('flex items-center gap-4 border-b border-line-subtle px-4', rowH)}
                >
                  <Skeleton className="h-3 w-12" />
                  <Skeleton className="h-3 flex-[3]" />
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-5 w-5 rounded-full" />
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-5 w-5 rounded-full" />
                  <Skeleton className="h-3 w-12" />
                </div>
              ))}
            </div>
          )}

          {list.data && rows.length === 0 && (
            <div className="overflow-hidden rounded-lg border border-line bg-surface">
              {/* An empty list has to say WHY it is empty. "Nothing in this
                  view" plus a hint left somebody staring at a group that has
                  187 open tickets, because a `view` chosen in another group
                  had survived the switch — and the hint it showed described
                  the filter rather than naming it. */}
              <EmptyState
                icon={view === 'unassigned' ? <UserX /> : <Inbox />}
                title={
                  activeFilterLabels.length === 0
                    ? `${cfg.data?.name ?? 'This group'} has no tickets yet`
                    : 'No tickets match these filters'
                }
                hint={
                  activeFilterLabels.length === 0
                    ? 'Nothing has arrived here. Raise one, or simulate an inbound message from Intake.'
                    : `Filtering by ${activeFilterLabels.join(' · ')}. Other tickets in ${cfg.data?.name ?? 'this group'} may not match.`
                }
                action={
                  activeFilterLabels.length > 0 ? (
                    <Button variant="secondary" size="sm" onClick={clearFilters}>
                      Clear filters
                    </Button>
                  ) : undefined
                }
              />
            </div>
          )}

          {list.data && rows.length > 0 && mode === 'board' && (
            <>
              <p className="mb-3 text-2xs leading-relaxed text-ink-tertiary">
                Drag a card between columns to change its state. Dropping one into a waiting column
                asks who you are waiting on — the same rule the API enforces. Right-click any card
                for more.
              </p>
              <Board
                tenant={tenant}
                rows={rows}
                config={cfg.data}
                meOid={me.data?.oid}
                actions={actions}
                onPeek={setPeekNumber}
              />
            </>
          )}

          {list.data && rows.length > 0 && mode === 'list' && (
            <div className="card-soft overflow-hidden rounded-lg">
              <div className="scroll-x">
                {canBulk && picked.size > 0 && (
                  <BulkBar
                    tenant={tenant}
                    numbers={[...picked]}
                    meOid={me.data?.oid}
                    onClear={() => setPicked(new Set())}
                    queues={hasCapability(me.data, tenant, 'ticket.queue.move') ? (cfg.data?.queues ?? []) : []}
                  />
                )}
                <table className="w-full min-w-[1080px] border-collapse text-left">
                  <thead className="glass sticky top-0 z-10">
                    <tr className="border-b border-line text-2xs uppercase tracking-wide text-ink-tertiary">
                      {canBulk && (
                        <th className="w-8 pl-4">
                          <input
                            type="checkbox"
                            aria-label="Select all"
                            checked={rows.length > 0 && rows.every((r) => picked.has(r.number))}
                            onChange={(e) => setPicked(e.target.checked ? new Set(rows.map((r) => r.number)) : new Set())}
                          />
                        </th>
                      )}
                      <Th
                        className="w-[78px] pl-5"
                        sortKey="ticket"
                        onSort={headerSort}
                        icon={sortIcon('ticket')}
                      >
                        Ticket
                      </Th>
                      <Th>Subject</Th>
                      <Th className="w-[124px]">Type</Th>
                      <Th className="w-[118px]">Queue</Th>
                      <Th className="w-[86px]">People</Th>
                      <Th className="w-[104px]">State</Th>
                      <Th
                        className="w-[124px]"
                        sortKey="sla"
                        onSort={headerSort}
                        icon={sortIcon('sla')}
                      >
                        SLA
                      </Th>
                      <Th className="w-[74px] text-right">Blocked</Th>
                      <Th
                        className="w-[82px] pr-5 text-right"
                        sortKey="updated"
                        onSort={headerSort}
                        icon={sortIcon('updated')}
                      >
                        Updated
                      </Th>
                    </tr>
                  </thead>

                  <tbody>
                    {rows.map((t, i) => {
                      const paused = t.state.startsWith('pending_');
                      const selected = i === cursor;
                      const peeked = peekNumber === t.number;
                      // The row's leading edge: priority, just-changed, cursor.
                      // It sits on whichever cell is outermost, so with the
                      // select column shown it is not jammed against the box.
                      // A color this person gave rows like this one (087).
                      const tint = rowColorFor(t, queuePrefs.prefs.colors);
                      const plainEdge = t.priority !== 'critical' && !(changedRows.has(t.id) && !peeked && !selected) && !peeked && !selected;
                      const edge = (
                        <span
                          aria-hidden
                          // Their color, at full strength, where nothing
                          // more urgent is using the edge.
                          style={tint && plainEdge ? { background: ROW_COLOR_HEX[tint] } : undefined}
                          className={cn(
                            'absolute inset-y-0 left-0 transition-opacity',
                            // Critical widens the edge and turns it red,
                            // which is what carries across peripheral
                            // vision while scrolling — before any text on
                            // the row has been read.
                            t.priority === 'critical'
                              ? 'w-[3px] bg-danger opacity-100'
                              : // A CHANGED ROW LIGHTS ITS LEADING EDGE,
                                // in the same blue as the wash. The eye
                                // enters a row at the left, so a mark
                                // there is read before the row is; and
                                // because the bar already carries a
                                // transition, it fades out on its own the
                                // moment the mark expires -- no second
                                // animation, no second duration to keep
                                // in step. Below the cursor, deliberately:
                                // where you ARE beats what moved.
                                changedRows.has(t.id) && !peeked && !selected
                                ? 'w-[2px] bg-info opacity-100'
                                : tint && plainEdge
                                  ? 'w-[3px] opacity-100'
                                  : cn(
                                      'w-[2px] bg-brand',
                                      peeked || selected ? 'opacity-100' : 'opacity-0',
                                    ),
                          )}
                        />
                      );
                      // Share of the target consumed, derived from worked time
                      // against the due date the API already computed.
                      const consumed = slaConsumed(t);

                      // Left takes it, right raises the heat. Both are in the
                      // right-click menu too — this is a faster route to them,
                      // never the only one.
                      const mine = t.assignee_oid === me.data?.oid;
                      const target = {
                        number: t.number,
                        state: t.state,
                        priority: t.priority,
                        assigneeOid: t.assignee_oid ?? null,
                      };
                      const sw = swipe.row(
                        t.id,
                        {
                          label: 'Assign to me',
                          enabled: Boolean(me.data?.oid) && !mine,
                          run: () =>
                            actions.assign.mutate({ number: t.number, oid: me.data!.oid }),
                        },
                        {
                          // Opens the SAME dialog the right-click menu opens.
                          // A resolve still has to say what fixed it and fill
                          // whatever the group requires at close — the swipe
                          // is a route to that dialog, never a way around it.
                          label: 'Mark resolved',
                          enabled: legalTransitionsFrom(t.state).includes('resolved'),
                          run: () => actions.move(target, 'resolved'),
                        },
                      );

                      return (
                        <TicketContextMenu
                          key={t.id}
                          tenant={tenant}
                          target={{
                            number: t.number,
                            state: t.state,
                            priority: t.priority,
                            assigneeOid: t.assignee_oid ?? null,
                            queueKey: t.queue_key,
                          }}
                          config={cfg.data}
                          meOid={me.data?.oid}
                          actions={actions}
                          onPeek={() => setPeekNumber(t.number)}
                        >
                        <tr
                          ref={(el) => {
                            rowRefs.current[i] = el;
                          }}
                          {...sw.handlers}
                          data-swipe={sw.side ? 'dragging' : 'idle'}
                          // A color this person gave rows like this one.
                          // Glass paints cells clear at rest, so it reads the
                          // translucent --row-tint instead (theme-glass.css).
                          data-tint={tint && !peeked && !selected && t.priority !== 'critical' ? tint : undefined}
                          draggable={false}
                          onDragStart={(e) => e.preventDefault()}
                          // A drag ends in a click. Without this every swipe
                          // would also open the ticket it just acted on.
                          onClick={() => {
                            if (swipe.swallowedClick()) return;
                            setCursor(i);
                            setPeekNumber(t.number);
                          }}
                          style={
                            {
                              touchAction: 'pan-y',
                              // The cells read these; the row itself does not
                              // move. `--row-bg` is what the cells paint over
                              // the tint, so the gap is the only place it
                              // shows through.
                              '--swipe-dx': `${sw.translateX}px`,
                              ...(tint ? { '--row-tint': rowTintClear(tint) } : {}),
                              '--row-bg': peeked
                                ? 'rgb(var(--brand-tint))'
                                : selected
                                  ? 'rgb(var(--surface-hover))'
                                  : t.priority === 'critical'
                                    ? 'rgb(var(--danger-tint) / 0.4)'
                                    : tint
                                      ? rowTint(tint)
                                      : 'rgb(var(--surface))',
                            } as React.CSSProperties
                          }
                          className={cn(
                            'group cursor-pointer border-b border-line-subtle transition-colors duration-fast',
                            rowH,
                            // Fades once and stops. Applied before the swipe
                            // tints below so a row being dragged shows the
                            // drag, not a stale update wash.
                            changedRows.has(t.id) && 'anim-row-changed',
                            // The tint the gap reveals. Assign is the
                            // brand color; resolve is the settled green,
                            // matching what the state badge becomes.
                            sw.side === 'left'
                              ? 'bg-brand-tint'
                              : sw.side === 'right'
                                ? 'bg-ok-tint'
                                : peeked
                              ? 'bg-brand-tint'
                              : selected
                                ? 'bg-surface-hover'
                                : // A critical row is tinted even when it is
                                  // not the one under the cursor. The pinned
                                  // block at the top has to be visible AS a
                                  // block, not as a run of rows you notice
                                  // one at a time.
                                  t.priority === 'critical'
                                  ? 'bg-danger-tint/40 hover:bg-danger-tint/60'
                                  : 'hover:bg-surface-hover',
                          )}
                        >
                          {canBulk && (
                            <td className="relative w-8 pl-4" onClick={(e) => e.stopPropagation()}>
                              {edge}
                              <input
                                type="checkbox"
                                aria-label={`Select #${t.number}`}
                                checked={picked.has(t.number)}
                                onChange={() => togglePick(t.number)}
                              />
                            </td>
                          )}
                          {/* Selected-row marker, so keyboard focus is visible. */}
                          <Td className="relative pl-4">
                            {!canBulk && edge}
                            <span className="nums block truncate text-sm font-medium text-brand">
                              #{t.number}
                            </span>
                            {/* IN THE GAP, not on the row. The cells have
                                moved right; this counter-translates so it
                                stays at the table's edge, sitting in the
                                space they left. Shown from the moment a
                                direction locks — "keep dragging" is what
                                tells somebody the gesture is real before
                                they have gone far enough to commit. */}
                            {sw.side === 'right' && sw.action && (
                              <span
                                aria-hidden
                                className={cn(
                                  'swipe-label pointer-events-none absolute inset-y-0 left-0 z-0 flex items-center whitespace-nowrap pl-1 text-2xs font-semibold uppercase tracking-wide text-ok',
                                  sw.past ? 'opacity-100' : 'opacity-60',
                                )}
                              >
                                {sw.past ? `Drop to ${sw.action.label.toLowerCase()}` : 'Keep dragging…'}
                              </span>
                            )}
                            <span className="block text-3xs uppercase tracking-wide text-ink-tertiary">
                              {t.intake_source}
                            </span>
                          </Td>

                          {/* w-full max-w-0 is the recipe that makes a table
                              cell actually truncate. `truncate` alone sets
                              nowrap, which makes the cell's PREFERRED width the
                              whole subject line — so under table-layout:auto
                              the column grew to fit it and pushed the table
                              past its minimum, clipping the right-hand columns
                              whenever the peek pane was open. max-w-0 lets this
                              one column absorb the slack instead. */}
                          <Td className="w-full max-w-0">
                            <div className="min-w-0">
                              <p className="flex items-center gap-1.5 text-sm text-ink">
                                {/* BESIDE THE SUBJECT, not down in the meta
                                    line. A ticket that came back on its own
                                    has to be distinguishable at a glance from
                                    one that was open all along, and the meta
                                    line is where things go to be skimmed
                                    past. It is `shrink-0` so the truncating
                                    subject never eats it. */}
                                {/* UPDATED SINCE YOU LOOKED, and it stays
                                    until you look again.

                                    The flash beside it is for somebody
                                    watching the screen when the change
                                    lands; this is for somebody who was at
                                    lunch. A ticket nobody has ever opened
                                    carries no dot — see the note on
                                    `unread` — so the mark means "you have
                                    read this one, and it moved". */}
                                {t.unread && (
                                  <span className="flex shrink-0 items-center">
                                    <span
                                      aria-hidden
                                      className="h-1.5 w-1.5 rounded-full bg-brand"
                                    />
                                    <span className="sr-only">
                                      Updated since you last opened it
                                    </span>
                                  </span>
                                )}
                                <span className={cn('truncate', t.unread && 'font-medium')}>
                                  {t.subject}
                                </span>
                                <span className="shrink-0">
                                  <EventBadge
                                    firedAt={t.event_fired_at}
                                    dueAt={t.event_due_at}
                                    eventName={t.event_name}
                                  />
                                </span>
                                <span className="shrink-0">
                                  <FamilyBadge
                                    parentNumber={t.parent_number}
                                    childNumbers={(t.child_numbers ?? []).map(Number)}
                                    relatedNumbers={(t.related_numbers ?? []).map(Number)}
                                  />
                                </span>
                              </p>
                              <p className="mt-0.5 flex items-center gap-2 truncate text-2xs text-ink-tertiary">
                                {/* The star sits OUTSIDE the truncating span:
                                    inside it, a long requester name would clip
                                    the one mark on the row that changes how it
                                    should be read. */}
                                <span className="flex min-w-0 items-center gap-1">
                                  {t.requester_vip && (
                                    <VipBadge title={t.requester_title} compact />
                                  )}
                                  <span className="truncate">
                                    {personLabel(t.requester_name, t.requester_email)}
                                  </span>
                                </span>
                                {t.project_numbers[0] && (
                                  <span className="nums shrink-0 font-mono">
                                    {t.project_numbers[0]}
                                  </span>
                                )}
                                {Number(t.reply_count) > 1 && (
                                  <span className="inline-flex shrink-0 items-center gap-0.5">
                                    <MessageSquare aria-hidden className="h-2.5 w-2.5" />
                                    {t.reply_count}
                                  </span>
                                )}
                                {t.subject_email && (
                                  <span className="shrink-0">for {t.subject_email}</span>
                                )}
                                {/* Tags: click one to see only tickets with it. */}
                                {t.tags.slice(0, 3).map((tag) => (
                                  <button
                                    key={tag}
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setParam('tag', tag);
                                    }}
                                    className="shrink-0 rounded-full border border-line px-1.5 text-3xs text-ink-secondary hover:border-brand hover:text-ink"
                                    title={`Only tickets tagged “${tag}”`}
                                  >
                                    {tag}
                                  </button>
                                ))}
                                {t.tags.length > 3 && <span className="shrink-0">+{t.tags.length - 3}</span>}
                              </p>
                            </div>
                          </Td>

                          <Td>
                            <Tooltip content={t.request_type ?? 'Unclassified'}>
                              <span className="block truncate text-xs text-ink-secondary">
                                {shortRequestType(t.request_type)}
                              </span>
                            </Tooltip>
                          </Td>

                          <Td>
                            <span className="block truncate text-xs text-ink-secondary">
                              {shortQueue(t.queue_name)}
                            </span>
                          </Td>

                          <Td>
                            {/* Two 20px avatars at a real gap. They used to
                                overlap by 6px, which collided the initials of
                                any two-letter pair and read as one smudge. */}
                            <span className="flex items-center gap-1.5">
                              <Tooltip
                                content={`Requester: ${personLabel(t.requester_name, t.requester_email)}`}
                              >
                                <span className="relative z-10">
                                  <Avatar
                                    name={t.requester_name}
                                    email={t.requester_email}
                                    size="sm"
                                    className="ring-2 ring-surface"
                                  />
                                </span>
                              </Tooltip>
                              {t.assignee_email ? (
                                <Tooltip
                                  content={`Owner: ${personLabel(t.assignee_name, t.assignee_email)}${
                                    t.assignee_away ? ` · ${awayLabel(t.assignee_away_until)}` : ''
                                  }`}
                                >
                                  <span className="relative">
                                    <Avatar
                                      name={t.assignee_name}
                                      email={t.assignee_email}
                                      size="sm"
                                      className={cn('ring-2 ring-surface', t.assignee_away && 'opacity-60')}
                                    />
                                    {/* AWAY: their automatic replies are on,
                                        so this ticket is waiting on somebody
                                        who is not there. On the owner's own
                                        face, where the eye already goes to
                                        see who has it. */}
                                    {t.assignee_away && (
                                      <span
                                        aria-label={awayLabel(t.assignee_away_until)}
                                        className="absolute -bottom-1 -right-1 flex h-3 w-3 items-center justify-center rounded-full bg-warn ring-2 ring-surface"
                                      >
                                        <Plane aria-hidden className="h-2 w-2 text-white" />
                                      </span>
                                    )}
                                  </span>
                                </Tooltip>
                              ) : (
                                <Tooltip content="Nobody owns this yet">
                                  <span className="flex h-5 w-5 items-center justify-center rounded-full border border-dashed border-warn/70 bg-warn-tint ring-2 ring-surface">
                                    <UserX aria-hidden className="h-2.5 w-2.5 text-warn" />
                                  </span>
                                </Tooltip>
                              )}

                              {/* SOMEBODY HAS THIS OPEN RIGHT NOW.
                                  In the same face pile as the requester and
                                  the owner, because it is the same kind of
                                  fact about people, and a column of its own
                                  would cost width on every row for something
                                  true of one. The brand ring is what
                                  separates "is here now" from "owns it" —
                                  the two must not read alike, or the pile
                                  stops meaning anything. */}
                              {viewersOf(Number(t.number)).slice(0, 2).map((v) => (
                                <Tooltip
                                  key={v.oid}
                                  content={`${personLabel(v.name, v.email)} has this open now`}
                                >
                                  <span className="relative">
                                    <Avatar
                                      name={v.name}
                                      email={v.email}
                                      size="sm"
                                      className="ring-2 ring-brand"
                                    />
                                  </span>
                                </Tooltip>
                              ))}
                              {viewersOf(Number(t.number)).length > 2 && (
                                <Tooltip
                                  content={viewersOf(Number(t.number))
                                    .map((v) => personLabel(v.name, v.email))
                                    .join(', ')}
                                >
                                  <span className="nums flex h-5 items-center rounded-full bg-brand-tint px-1 text-2xs font-medium text-brand ring-2 ring-surface">
                                    +{viewersOf(Number(t.number)).length - 2}
                                  </span>
                                </Tooltip>
                              )}
                            </span>
                          </Td>

                          <Td>
                            <span className="flex flex-wrap items-center gap-1.5">
                              {/* The WORD for critical, an icon for the rest.
                                  An icon needs a legend; the word needs
                                  nothing, and survives a screenshot pasted
                                  into a chat. */}
                              {t.priority === 'critical' ? (
                                <Badge tone="danger" icon={<Chilli />}>
                                  Critical
                                </Badge>
                              ) : (
                                <PriorityIcon priority={t.priority} />
                              )}
                              <StateChip state={t.state} />
                            </span>
                          </Td>

                          <Td>
                            <span className="flex items-center gap-2">
                              <SlaRing
                                consumed={consumed}
                                breached={t.resolution_breached}
                                paused={paused}
                                verdict={slaVerdict(t)}
                              />
                              <span className="min-w-0">
                                <span
                                  className={cn(
                                    'block truncate text-xs font-medium',
                                    isSettled(t.state)
                                      ? t.resolution_breached
                                        ? 'text-danger'
                                        : 'text-ink-secondary'
                                      : t.resolution_breached
                                        ? 'text-danger'
                                        : paused
                                          ? 'text-ink-tertiary'
                                          : 'text-ink',
                                  )}
                                >
                                  {slaText(t, paused)}
                                </span>
                                <span className="nums block truncate text-3xs text-ink-tertiary">
                                  {minutes(t.running_minutes)} worked
                                </span>
                              </span>
                            </span>
                          </Td>

                          <Td className="text-right">
                            {/* A row that says "Paused" must not also say
                                there is no blocked time — show the figure
                                whenever the ticket is actually waiting. */}
                            {t.paused_minutes > 0 || paused ? (
                              <span className="nums text-xs text-accent">
                                {minutes(t.paused_minutes)}
                              </span>
                            ) : (
                              <span className="text-xs text-ink-tertiary">—</span>
                            )}
                          </Td>

                          <Td className="relative pr-4 text-right">
                            {/* The other gap. Dragging left opens space at the
                                right-hand edge, so the label lives here. */}
                            {sw.side === 'left' && sw.action && (
                              <span
                                aria-hidden
                                className={cn(
                                  'swipe-label pointer-events-none absolute inset-y-0 right-0 z-0 flex items-center whitespace-nowrap pr-1 text-2xs font-semibold uppercase tracking-wide text-brand',
                                  sw.past ? 'opacity-100' : 'opacity-60',
                                )}
                              >
                                {sw.past ? `Drop to ${sw.action.label.toLowerCase()}` : 'Keep dragging…'}
                              </span>
                            )}
                            <span className="nums whitespace-nowrap text-xs text-ink-tertiary">
                              {ago(t.updated_at)}
                            </span>
                            {t.needs_human_routing && (
                              <span className="mt-0.5 block">
                                <Badge tone="warn">Route</Badge>
                              </span>
                            )}
                          </Td>
                        </tr>
                        </TicketContextMenu>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between gap-3 border-t border-line bg-surface-sunken/60 px-4 py-2">
                <p className="flex items-center gap-3 text-3xs text-ink-tertiary">
                  <span className="flex items-center gap-1">
                    <Kbd>J</Kbd>
                    <Kbd>K</Kbd> move
                  </span>
                  <span className="flex items-center gap-1">
                    <Kbd>↵</Kbd> preview
                  </span>
                  <span className="flex items-center gap-1">
                    <Kbd>O</Kbd> open
                  </span>
                </p>
                {/* REAL NAVIGATION. This said "narrow the filters to see
                    further", which is not navigation and is unusable against
                    48,015 tickets: the reader could see one page and had no
                    way to reach the rest. */}
                <div className="flex items-center gap-2">
                  {pages.length > 0 && (
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => setPages((p) => p.slice(0, -1))}
                    >
                      Previous
                    </Button>
                  )}
                  {pages.length > 0 && (
                    <span className="nums text-3xs text-ink-tertiary">
                      page {pages.length + 1}
                    </span>
                  )}
                  {list.data.nextCursor !== null && (
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => {
                        const next = list.data?.nextCursor;
                        if (next) setPages((p) => [...p, next]);
                      }}
                    >
                      Next
                    </Button>
                  )}
                  {list.data.nextCursor === null && rows.length >= 80 && (
                    // The sort has no keyset the database can express —
                    // `due_asc` puts undated tickets after every dated one,
                    // and no comparison against null says that. Better to
                    // name the reason than to offer a Next that repeats.
                    <span className="text-3xs text-ink-tertiary">
                      More available — sort by newest or oldest to page through
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          <p className="mt-3 max-w-3xl text-2xs leading-relaxed text-ink-tertiary">
            <strong className="text-ink-secondary">Worked</strong> is business time in an active
            state. <strong className="text-ink-secondary">Blocked</strong> is time waiting on
            somebody else, which does not consume the target. Keeping them apart is what makes the
            long tail explainable.
          </p>
        </div>
      </div>

      {/* ---------------------------------------------------------------- */}
      {actions.reasonDialog}
      {/* Rendered beside its sibling, once for the page: a dialog mounted
          inside a context menu closes with the menu that opened it. */}
      {actions.linkDialog}
      {actions.queueDialog}
      {actions.manageDialog}

      {peekNumber !== null && (
        <TicketPeek
          tenant={tenant}
          number={peekNumber}
          onClose={() => setPeekNumber(null)}
        />
      )}
    </div>
  );
}

/**
 * What the SLA column says.
 *
 * Split out of the cell because the old expression had no branch for a
 * finished ticket, so it fell through to the countdown and told the reader
 * a ticket closed in 2020 was "Due now".
 */
function slaText(t: TicketListRow, paused: boolean): string {
  const verdict = slaVerdict(t);
  if (verdict === 'met') return 'Met';
  if (verdict === 'missed') return 'Missed';
  if (verdict === 'cancelled') return 'Cancelled';
  if (verdict === 'no-target') return 'No target';
  if (t.resolution_breached) return 'Overdue';
  if (paused) return 'Paused';
  return dueLabel(t.resolution_due_at);
}

function dueLabel(dueAt: string | null): string {
  if (!dueAt) return 'No target';
  const ms = new Date(dueAt).getTime() - Date.now();
  if (ms <= 0) return 'Due now';
  const h = ms / 3_600_000;
  if (h < 1) return `${Math.max(1, Math.round(ms / 60_000))}m left`;
  if (h < 24) return `${Math.round(h)}h left`;
  return `${Math.round(h / 24)}d left`;
}

/** Compact state pill. Distinct from the full StateBadge used on detail. */
function StateChip({ state }: { state: TicketListRow['state'] }) {
  /**
   * EXHAUSTIVE OVER `TicketState`, not `Record<string, _>`.
   *
   * It was keyed on `string` with a `?? map.open` fallback, so a state the
   * map had never heard of rendered as "Open" — a parked ticket claiming to
   * be actively worked, with nothing to notice. The compiler is the only
   * thing that reliably catches the next state added.
   */
  const map: Record<TicketListRow['state'], { label: string; cls: string }> = {
    new: { label: 'New', cls: 'bg-info-tint text-info ring-info-line/50' },
    open: { label: 'Open', cls: 'bg-brand-tint text-brand ring-brand-line/50' },
    pending_requester: { label: 'Requester', cls: 'bg-surface-sunken text-ink-secondary ring-line' },
    pending_internal: { label: 'Internal', cls: 'bg-surface-sunken text-ink-secondary ring-line' },
    pending_external: { label: 'External', cls: 'bg-accent-tint text-accent ring-accent-line/50' },
    pending_event: { label: 'Event', cls: 'bg-surface-sunken text-ink-secondary ring-line' },
    resolved: { label: 'Resolved', cls: 'bg-ok-tint text-ok ring-ok-line/50' },
    closed: { label: 'Closed', cls: 'bg-surface-sunken text-ink-tertiary ring-line' },
    cancelled: { label: 'Cancelled', cls: 'bg-surface-sunken text-ink-tertiary ring-line line-through' },
  };
  const s = map[state];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-sm px-1.5 py-px text-2xs font-medium ring-1 ring-inset',
        s.cls,
      )}
    >
      <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-current opacity-75" />
      {s.label}
    </span>
  );
}

function Th({
  children,
  className,
  sortKey,
  onSort,
  icon,
}: {
  children?: React.ReactNode;
  className?: string;
  sortKey?: string;
  onSort?: (key: string) => void;
  icon?: React.ReactNode;
}) {
  if (!sortKey || !onSort) {
    return (
      <th scope="col" className={cn('px-3 py-2.5 font-semibold', className)}>
        {children}
      </th>
    );
  }
  return (
    <th scope="col" className={cn('px-3 py-2.5 font-semibold', className)}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={cn(
          'group -mx-1 inline-flex items-center gap-1 rounded-xs px-1 uppercase transition-colors hover:text-ink',
          className?.includes('text-right') && 'flex-row-reverse',
        )}
      >
        {children}
        {icon}
      </button>
    </th>
  );
}

function Td({ children, className }: { children?: React.ReactNode; className?: string }) {
  return <td className={cn('px-3 align-middle', className)}>{children}</td>;
}
